import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react'
import Map, { MapRef, Marker, Popup } from 'react-map-gl/mapbox'
import 'mapbox-gl/dist/mapbox-gl.css'
import { useTheme } from '@react-navigation/native'
import Config from '@config'
import { useLanguage } from '@context'
import { instagramMapStyle } from '../mapStyle'

const MIN_ZOOM = 1
const MAX_ZOOM = 18
// Kept free on the right of the zoom bar for the contacts panel's open/close
// arrow, which sticks out over the map.
const PANEL_TOGGLE_ROOM = 24

// Halo that pulses around my own marker (still, when the OS asks for reduced motion).
const MY_POSITION_CSS = `
@keyframes kt-map-me-pulse {
  from { transform: scale(1); opacity: 0.45; }
  to { transform: scale(2.3); opacity: 0; }
}
.kt-map-me-pulse { animation: kt-map-me-pulse 2s ease-out infinite; }
@media (prefers-reduced-motion: reduce) {
  .kt-map-me-pulse { animation: none; transform: scale(1.5); opacity: 0.25; }
}
`

interface WebMapProps {
  /** [latitude, longitude] the map flies to whenever it changes. */
  center: [number, number]
  zoom?: number
  style?: React.CSSProperties
  contacts: any[]
  onContactClick: (contactId: string) => void
  /** Space on the right covered by the contacts panel (+ a 16px margin). */
  rightOffset?: number
  /** Me (live or saved position), or nothing when my position is unknown. */
  me?: { lat: number; lng: number; avatarUrl?: string } | null
  /** I chose to be invisible on my contacts' maps. */
  isHidden?: boolean
  /** Switches my visibility (eye button); no button while it is unknown. */
  onToggleVisibility?: () => void
  isVisibilityPending?: boolean
}

const WebMap = forwardRef(
  (
    {
      center,
      zoom,
      style,
      contacts,
      onContactClick,
      rightOffset = 16,
      me,
      isHidden = false,
      onToggleVisibility,
      isVisibilityPending = false,
    }: WebMapProps,
    ref,
  ) => {
    const { t } = useLanguage()
    const theme = useTheme()
    // My own marker (and the button that brings me back to it) use the app's
    // blue, and turn grey while I am invisible to my contacts.
    const meColor = isHidden ? theme.colors.greyBlue : theme.colors.primary
    const mapRef = useRef<any>(null)
    const [hoveredContactId, setHoveredContactId] = useState<string | null>(null)
    const [currentZoom, setCurrentZoom] = useState<number>(zoom ?? 6)
    // The map is created asynchronously: a center that changes before it
    // exists (my position can be known at once) is applied as soon as it does.
    const [isMapReady, setIsMapReady] = useState(false)
    const setMapRef = useCallback((map: MapRef | null) => {
      mapRef.current = map
      setIsMapReady(map != null)
    }, [])
    // The contacts panel covers the right of the map (rightOffset = its width
    // + a 16px margin): fly targets land in the middle of the visible part.
    const visibleCenterOffset = useRef<[number, number]>([0, 0])
    visibleCenterOffset.current = [-Math.max(0, rightOffset - 16) / 2, 0]

    React.useEffect(() => {
      if (!isMapReady) return
      mapRef.current?.flyTo({
        center: [center[1], center[0]],
        zoom: zoom ?? 6,
        duration: 1500,
        offset: visibleCenterOffset.current,
      })
    }, [center, zoom, isMapReady])

    // Keep the slider in sync with native gestures (scroll / pinch) without
    // re-creating the handler on every render of the map.
    const onZoom = useCallback((e: any) => setCurrentZoom(e.viewState.zoom), [])

    // Drive the map zoom from the right-hand slider / +- buttons, around the
    // middle of the visible part of the map.
    const applyZoom = useCallback((next: number, duration = 0) => {
      const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, next))
      setCurrentZoom(clamped)
      mapRef.current?.zoomTo(clamped, { duration, offset: visibleCenterOffset.current })
    }, [])

    const zoomToContact = useCallback((contact: any) => {
      mapRef.current?.flyTo({
        center: [contact.lng, contact.lat],
        zoom: 15,
        duration: 1000,
        offset: visibleCenterOffset.current,
      })
    }, [])

    useImperativeHandle(ref, () => ({ zoomToContact }), [zoomToContact])

    // Bring the map back on me, keeping the current zoom so the people around
    // stay in view.
    const locateMe = useCallback(() => {
      if (!me) return
      mapRef.current?.flyTo({
        center: [me.lng, me.lat],
        duration: 1000,
        offset: visibleCenterOffset.current,
      })
    }, [me])

    const hoveredContact = hoveredContactId
      ? contacts.find((c: any) => c.id === hoveredContactId)
      : null

    return (
      <div style={{ ...style, position: 'relative' }}>
        <Map
          ref={setMapRef}
          initialViewState={{
            latitude: center[0],
            longitude: center[1],
            zoom: zoom ?? 6,
          }}
          mapboxAccessToken={Config.MAPBOX_ACCESS_TOKEN}
          mapStyle={JSON.parse(instagramMapStyle)}
          projection="globe"
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          onZoom={onZoom}
          style={{ width: '100%', height: '100%' }}
        >
          {contacts.map((contact: any) => (
            <Marker
              key={contact.id}
              latitude={contact.lat}
              longitude={contact.lng}
              anchor="center"
              onClick={() => onContactClick(contact.id)}
            >
              <div
                onMouseEnter={() => setHoveredContactId(contact.id)}
                onMouseLeave={() => setHoveredContactId(null)}
                style={{ position: 'relative', cursor: 'pointer' }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    // Ghost markers get a purple ring to stand out from real contacts.
                    border: contact.isGhost ? '3px solid #9C27B0' : '3px solid white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    backgroundImage: contact.avatarUrl ? `url('${contact.avatarUrl}')` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                />
                {contact.isGhost && (
                  <div
                    style={{
                      position: 'absolute',
                      right: -4,
                      bottom: -4,
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: 'white',
                      border: '1px solid #9C27B0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10,
                      lineHeight: '14px',
                    }}
                  >
                    👻
                  </div>
                )}
              </div>
            </Marker>
          ))}

          {me && (
            // Drawn above the contacts (zIndex) so I stay visible when someone
            // is at the same place.
            <Marker latitude={me.lat} longitude={me.lng} anchor="center" style={{ zIndex: 2 }}>
              <div
                data-testid="map-my-position"
                data-hidden={isHidden}
                role="img"
                aria-label={t(isHidden ? 'mapScreen.myPositionHidden' : 'mapScreen.myPosition')}
                title={t(isHidden ? 'mapScreen.myPositionHidden' : 'mapScreen.myPosition')}
                style={{ position: 'relative', width: 44, height: 44 }}
              >
                {/* No pulsing halo while I am invisible to my contacts. */}
                {!isHidden && (
                  <div
                    className="kt-map-me-pulse"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '50%',
                      background: meColor,
                    }}
                  />
                )}
                <div
                  style={{
                    position: 'relative',
                    boxSizing: 'border-box',
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    border: `4px solid ${meColor}`,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
                    backgroundColor: meColor,
                    backgroundImage: me.avatarUrl ? `url('${me.avatarUrl}')` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                >
                  {/* No photo: a white dot in the middle, like a GPS "you are here". */}
                  {!me.avatarUrl && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: 14,
                        height: 14,
                        marginTop: -7,
                        marginLeft: -7,
                        borderRadius: '50%',
                        background: 'white',
                      }}
                    />
                  )}
                </div>
                <div
                  style={{
                    position: 'absolute',
                    top: 48,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    padding: '1px 8px',
                    borderRadius: 10,
                    background: meColor,
                    color: 'white',
                    fontSize: 11,
                    fontWeight: 700,
                    lineHeight: '16px',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  }}
                >
                  {t('mapScreen.me')}
                </div>
              </div>
            </Marker>
          )}

          {hoveredContact && (
            <Popup
              latitude={hoveredContact.lat}
              longitude={hoveredContact.lng}
              anchor="bottom"
              offset={20}
              closeButton={false}
              closeOnClick={false}
              // Above my own marker (zIndex 2).
              style={{ zIndex: 3 }}
            >
              <div style={{ textAlign: 'center', minWidth: 120 }}>
                <strong>{hoveredContact.fullName}</strong>
                {hoveredContact.professions?.[0]?.name && (
                  <>
                    <br />
                    <span style={{ fontSize: 12, color: '#666' }}>
                      {hoveredContact.professions[0].name}
                    </span>
                  </>
                )}
              </div>
            </Popup>
          )}
        </Map>

        {/* Top horizontal zoom slider ( - / drag / + ), centered over the
          visible map (the panel area on the right is excluded via rightOffset,
          plus room for the panel's arrow). */}
        <div
          style={{
            position: 'absolute',
            top: 16,
            left: 16,
            right: rightOffset + PANEL_TOGGLE_ROOM,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 5,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
              padding: '8px 12px',
              background: 'white',
              borderRadius: 22,
              boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
              pointerEvents: 'auto',
              userSelect: 'none',
              // Shrinks the slider rather than overflowing on narrow screens.
              boxSizing: 'border-box',
              maxWidth: '100%',
              minWidth: 0,
            }}
          >
            <button
              type="button"
              aria-label={t('mapScreen.zoomOut')}
              onClick={() => applyZoom(currentZoom - 1, 250)}
              style={zoomBtnStyle}
            >
              -
            </button>
            <input
              type="range"
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={0.1}
              value={currentZoom}
              aria-label={t('mapScreen.zoomLevel')}
              onChange={(e) => applyZoom(Number(e.target.value), 0)}
              style={{ flex: '0 1 200px', minWidth: 48, cursor: 'pointer', accentColor: '#1b3a6b' }}
            />
            <button
              type="button"
              aria-label={t('mapScreen.zoomIn')}
              onClick={() => applyZoom(currentZoom + 1, 250)}
              style={zoomBtnStyle}
            >
              +
            </button>
            {(me || onToggleVisibility) && (
              <div style={{ width: 1, height: 22, flexShrink: 0, background: '#e0e0e0' }} />
            )}
            {me && (
              <button
                type="button"
                data-testid="map-locate-me"
                aria-label={t('mapScreen.locateMe')}
                title={t('mapScreen.locateMe')}
                onClick={locateMe}
                style={{ ...zoomBtnStyle, background: meColor }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="7" />
                  <circle cx="12" cy="12" r="2.5" fill="currentColor" />
                  <path d="M12 1v3M12 20v3M1 12h3M20 12h3" />
                </svg>
              </button>
            )}
            {onToggleVisibility && (
              // The eye: open = my contacts see me on their map, closed = invisible.
              <button
                type="button"
                data-testid="map-toggle-visibility"
                data-hidden={isHidden}
                aria-label={t(isHidden ? 'mapScreen.showMe' : 'mapScreen.hideMe')}
                title={t(isHidden ? 'mapScreen.showMe' : 'mapScreen.hideMe')}
                onClick={onToggleVisibility}
                disabled={isVisibilityPending}
                style={{
                  ...zoomBtnStyle,
                  background: meColor,
                  opacity: isVisibilityPending ? 0.6 : 1,
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  {isHidden ? (
                    // Closed eye: the lid and its lashes.
                    <path d="M2 10c2.8 3.3 6.2 5 10 5s7.2-1.7 10-5M5.2 13.3l-1.5 2M9.4 14.8l-.6 2.4M14.6 14.8l.6 2.4M18.8 13.3l1.5 2" />
                  ) : (
                    <>
                      <path d="M1.5 12S5.5 5 12 5s10.5 7 10.5 7-4 7-10.5 7S1.5 12 1.5 12z" />
                      <circle cx="12" cy="12" r="3" />
                    </>
                  )}
                </svg>
              </button>
            )}
          </div>
        </div>
        <style>{MY_POSITION_CSS}</style>
      </div>
    )
  },
)

const zoomBtnStyle: React.CSSProperties = {
  flexShrink: 0,
  width: 30,
  height: 30,
  borderRadius: 15,
  border: 'none',
  background: '#1b3a6b',
  color: 'white',
  fontSize: 18,
  lineHeight: '30px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

export default WebMap
